import {
    Box,
    Button,
    Checkbox,
    Divider,
    ListSubheader,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ModalityChip from '~/components/Chip/ModalityChip';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import ImageListMeta from './ImageListMeta';

/** An image the user can open in a row of its own. */
export interface OpenableImage {
    readonly seriesInstanceUid: SeriesInstanceUid;
    readonly modality: string;
    readonly name: string;
    readonly date: Date | undefined;
    readonly seriesNumber: number | undefined;
}

/** Height of the list before it scrolls. */
const LIST_MAX_HEIGHT_PX = 380;

/**
 * The images of the patient, each with a tick that says whether it has a
 * row in the viewport grid: the primary image first, as its row is first in
 * the grid, then the others in the order given. The ticks start from the rows shown now and are
 * a draft: Open asks for one row per ticked image, in this list's order,
 * and Cancel drops the draft. The primary image is always ticked: its row
 * is the one the sidebar names, changed with Change Image. Open stays
 * disabled while the draft is the rows shown now.
 */
export default function OpenDatasetsPanel({
    images: givenImages,
    shown,
    primarySeriesUid,
    onOpen,
    onCancel,
}: {
    images: ReadonlyArray<OpenableImage>;
    /** The images the rows show now. */
    shown: ReadonlyArray<SeriesInstanceUid>;
    primarySeriesUid: string | undefined;
    onOpen: (seriesInstanceUids: SeriesInstanceUid[]) => void;
    onCancel: () => void;
}) {
    const intl = useIntl();
    const isPrimaryImage = (image: OpenableImage) =>
        image.seriesInstanceUid === primarySeriesUid;
    const images = [
        ...givenImages.filter(isPrimaryImage),
        ...givenImages.filter((image) => !isPrimaryImage(image)),
    ];
    const isShownNow = (uid: SeriesInstanceUid) =>
        uid === primarySeriesUid || shown.includes(uid);
    const [ticked, setTicked] = useState(
        () =>
            new Set(
                images
                    .map((image) => image.seriesInstanceUid)
                    .filter(isShownNow)
            )
    );
    const unchanged = images.every(
        (image) =>
            ticked.has(image.seriesInstanceUid) ===
            isShownNow(image.seriesInstanceUid)
    );
    const primaryReason = intl.formatMessage({
        id: 'datasetPicker.primaryImageRow',
    });

    const toggle = (uid: SeriesInstanceUid) =>
        setTicked((current) => {
            const next = new Set(current);
            if (next.has(uid)) {
                next.delete(uid);
            } else {
                next.add(uid);
            }
            return next;
        });

    return (
        <>
            <ListSubheader component="div">
                <FormattedMessage id="datasetPicker.openDatasets" />
            </ListSubheader>
            <Box
                display="flex"
                flexDirection="column"
                maxHeight={LIST_MAX_HEIGHT_PX}
                overflow="auto"
                minHeight={0}
            >
                {images.map((image) => {
                    const isPrimary = isPrimaryImage(image);
                    const row = (
                        <Box
                            key={image.seriesInstanceUid}
                            component="label"
                            display="flex"
                            alignItems="center"
                            gap={1}
                            minHeight={28}
                            pr={1}
                            pl="2px"
                            minWidth={0}
                            data-testid="open-datasets-image"
                            sx={(theme) => ({
                                borderRadius: `${theme.shape.borderRadius}px`,
                                cursor: isPrimary ? 'default' : 'pointer',
                                '&:hover': isPrimary
                                    ? undefined
                                    : { bgcolor: 'action.hover' },
                            })}
                        >
                            <Checkbox
                                size="small"
                                checked={ticked.has(image.seriesInstanceUid)}
                                disabled={isPrimary}
                                onChange={() => toggle(image.seriesInstanceUid)}
                                sx={{ p: '4px' }}
                            />
                            <Box display="flex" flexShrink={0}>
                                <ModalityChip modality={image.modality} />
                            </Box>
                            <Typography
                                component="span"
                                flex={1}
                                minWidth={0}
                                fontSize={13}
                                fontWeight={500}
                                noWrap
                            >
                                <SensitiveInfoTypography>
                                    {image.name}
                                </SensitiveInfoTypography>
                            </Typography>
                            <ImageListMeta
                                date={image.date}
                                seriesNumber={image.seriesNumber}
                            />
                        </Box>
                    );
                    return isPrimary ? (
                        <Tooltip
                            key={image.seriesInstanceUid}
                            title={primaryReason}
                            placement="right"
                        >
                            {row}
                        </Tooltip>
                    ) : (
                        row
                    );
                })}
            </Box>
            <Divider />
            <Box display="flex" alignItems="center" gap={1} p="4px 4px 4px 8px">
                <Typography
                    component="span"
                    flex={1}
                    fontSize={12}
                    color="text.secondary"
                    data-testid="open-datasets-count"
                >
                    <FormattedMessage
                        id="datasetPicker.imageCount"
                        values={{
                            count: ticked.size,
                            number: (
                                <Typography
                                    variant="monoMeta"
                                    color="text.primary"
                                >
                                    {ticked.size}
                                </Typography>
                            ),
                        }}
                    />
                </Typography>
                <Button size="small" variant="text" onClick={onCancel}>
                    <FormattedMessage id="cancel" />
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disabled={unchanged}
                    onClick={() =>
                        onOpen(
                            images
                                .map((image) => image.seriesInstanceUid)
                                .filter((uid) => ticked.has(uid))
                        )
                    }
                    data-testid="open-datasets-open"
                >
                    <FormattedMessage id="datasetPicker.open" />
                </Button>
            </Box>
        </>
    );
}
